import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { handleError, handleSuccess, makeRequest } from '../utils/resource.util';
import { HttpMethod } from '../enums/http-method.enum';
import { ErrorService } from './error.service';
import { inject } from '@angular/core';
import { Page } from '@monorepo/interfaces';

export class ResourceService<T, C extends Partial<T>, U extends Partial<T> & { id?: string | number; oid?: string }> {
    private baseUrl = environment.apiConfig[1].uri;
    private _httpClient = inject(HttpClient);
    private _errorService = inject(ErrorService);

    constructor(private _resourceTypeName: string) {}

    public getAll(
        params?: HttpParams | { [param: string]: string | string[] },
        headers?: HttpHeaders | { [header: string]: string | string[] }
    ): Observable<T[]> {
        return makeRequest<T[]>(
            this._httpClient,
            HttpMethod.GET,
            `${this.baseUrl}/${this._resourceTypeName}`,
            { params, headers },
            handleError<T[]>(`${HttpMethod.GET} ${this._resourceTypeName}`, this._errorService)
        );
    }

    public getAllPaginated(
        page = 0,
        pageSize = 20,
        params?: HttpParams | { [param: string]: string | string[] },
        headers?:
            | HttpHeaders
            | {
                  [header: string]: string | string[];
              }
    ): Observable<Page<T>> {
        return makeRequest<Page<T>>(
            this._httpClient,
            HttpMethod.GET,
            `${this.baseUrl}/${this._resourceTypeName}?page=${page}&pageSize=${pageSize}`,
            { params, headers },
            handleError<Page<T>>(`${HttpMethod.GET} ${this._resourceTypeName} (paginated)`, this._errorService)
        );
    }

    public get(
        id: string | number,
        params?: HttpParams | { [param: string]: string | string[] },
        headers?: HttpHeaders | { [header: string]: string | string[] }
    ): Observable<T> {
        return makeRequest<T>(
            this._httpClient,
            HttpMethod.GET,
            `${this.baseUrl}/${this._resourceTypeName}/${id}`,
            { params, headers },
            handleError<T>(`${HttpMethod.GET} ${this._resourceTypeName}`, this._errorService)
        );
    }

    public create(resource: C, headers?: HttpHeaders | { [header: string]: string | string[] }): Observable<T> {
        return makeRequest<T>(
            this._httpClient,
            HttpMethod.POST,
            `${this.baseUrl}/${this._resourceTypeName}`,
            { body: resource, headers },
            handleError<T>(`${HttpMethod.POST} ${this._resourceTypeName}`, this._errorService),
            handleSuccess<T>(`${HttpMethod.POST} ${this._resourceTypeName}`, this._errorService)
        );
    }

    public update(resource: U, headers?: HttpHeaders | { [header: string]: string | string[] }): Observable<T> {
        return makeRequest<T>(
            this._httpClient,
            HttpMethod.PUT,
            `${this.baseUrl}/${this._resourceTypeName}/${resource.id ?? resource.oid}`,
            { body: resource, headers },
            handleError<T>(`${HttpMethod.PATCH} ${this._resourceTypeName}`, this._errorService),
            handleSuccess<T>(`${HttpMethod.PATCH} ${this._resourceTypeName}`, this._errorService)
        );
    }

    public delete(
        id: string | number,
        headers?: HttpHeaders | { [header: string]: string | string[] }
    ): Observable<void> {
        return makeRequest<void>(
            this._httpClient,
            HttpMethod.DELETE,
            `${this.baseUrl}/${this._resourceTypeName}/${id}`,
            { headers },
            handleError<void>(`${HttpMethod.DELETE} ${this._resourceTypeName}`, this._errorService),
            handleSuccess<void>(`${HttpMethod.DELETE} ${this._resourceTypeName}`, this._errorService)
        );
    }

    public customRequest<R>(
        method: HttpMethod,
        endpoint: string,
        options: {
            body?: unknown;
            params?: HttpParams | { [param: string]: string | string[] };
            headers?: HttpHeaders | { [header: string]: string | string[] };
            observe?: 'body' | 'response';
            responseType?: 'json' | 'text' | 'blob' | 'arraybuffer';
        } = {}
    ): Observable<R> {
        return makeRequest<R>(
            this._httpClient,
            method,
            `${this.baseUrl}/${endpoint}`,
            options,
            handleError<R>(`${method} ${endpoint}`, this._errorService)
        );
    }
}
